import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { API_URLS } from '../../constants/api-urls/api-urls';
import { skipSpinnerOptions } from '../../core/services/http-options.utils';

export interface CreateRequestPayload {
    request_type: 'Bugs' | 'Enhancements' | 'general questions';
    user_request: string;
    description: string;
    given_by: number;
    business_unit_code: string;
    region: string;
    attachment?: string;
    requested_by?: string;
}

export interface UpdateRequestPayload {
    request_type?: 'Bugs' | 'Enhancements';
    user_request?: string;
    description?: string;
    status?: string;
    attachment?: string;
}

export interface RequestResponse {
    id: number;
    request_type: string;
    user_request: string;
    description: string;
    status: string;
    given_by: number;
    business_unit_code: string;
    region: string;
    language: string;
    attachment?: string;
    user_type: string;
    created_date: string;
    updated_date: string;
    given_by_first_name?: string;
    given_by_last_name?: string;
    requested_by_first_name?: string;
    requested_by_last_name?: string;
    assigned_category?: string;
}

export interface ActivityLogPayload {
    request_id: number;
    activity_type: string;
    new_status: string;
    comments: string;
    given_by: number;
}

export interface ActivityLog {
    id: number;
    request_id: number;
    activity_type: string;
    description: string;
    old_status: string | null;
    new_status: string;
    comments: string;
    given_by: number;
    created_date: string;
    user_type: string;
    first_name?: string;
    last_name?: string;
    isUser?: boolean;
    given_by_first_name?: string;
    given_by_last_name?: string;
}

export interface RequestWithLogsResponse {
    request: RequestResponse;
    activity_logs: ActivityLog[];
}

export interface RequestStatusMetadata {
    id: number;
    name: string;
    feedback_value: string;
}

export interface RequestStatusMetadataResponse {
    data?: RequestStatusMetadata[];
}

@Injectable({
    providedIn: 'root'
})
export class ContactCenterApiService {

    constructor(private http: HttpClient) { }

    createRequest(file: File | null, payload: CreateRequestPayload): Observable<RequestResponse> {
        const formData: FormData = new FormData();
        formData.append('request_type', payload.request_type);
        formData.append('user_request', payload.user_request || '');
        formData.append('description', payload.description || '');
        formData.append('requested_by', payload.requested_by !== undefined ? String(payload.requested_by) : '');
        if (payload.given_by !== undefined) formData.append('given_by', String(payload.given_by));
        if (payload.business_unit_code) formData.append('business_unit_code', payload.business_unit_code);
        if (payload.region) formData.append('region', payload.region);
        if (file) {
            formData.append('attachment', file, file.name);
        }
        return this.http.post<RequestResponse>(
            API_URLS.CONTACT_CENTER.CREATE_REQUEST,
            formData,
            skipSpinnerOptions()
        );
    }

    getRequestList(): Observable<RequestResponse[]> {
        return this.http.get<RequestResponse[]>(API_URLS.CONTACT_CENTER.GET_REQUEST_LIST, skipSpinnerOptions());
    }

    updateRequestActivityLog(payload: ActivityLogPayload, file?: File | null): Observable<ActivityLog> {
        const formData: FormData = new FormData();
        formData.append('request_id', String(payload.request_id));
        formData.append('request_type', (payload as any).request_type || '');
        formData.append('activity_type', payload.activity_type || '');
        formData.append('new_status', payload.new_status || '');
        formData.append('comments', payload.comments || '');
        formData.append('given_by', String(payload.given_by));
        if (file) {
            formData.append('attachment', file, file.name);
        }
        return this.http.post<ActivityLog>(API_URLS.CONTACT_CENTER.UPDATE_REQUEST_ACTIVITY_LOG, formData, skipSpinnerOptions());
    }

    getRequestWithLogs(request_id: number): Observable<RequestWithLogsResponse> {
        return this.http.get<RequestWithLogsResponse>(API_URLS.CONTACT_CENTER.REQUEST_WITH_LOGS(request_id), skipSpinnerOptions());
    }

    fetchRequestWithLogsByUser(filters?: any): Observable<RequestResponse[]> {
        let params = new HttpParams();
        if (filters) {
            Object.keys(filters).forEach(key => {
                if (filters[key] !== null && filters[key] !== undefined) {
                    params = params.set(key, filters[key]);
                }
            });
        }
        return this.http.get<RequestResponse[]>(API_URLS.CONTACT_CENTER.FETCH_REQUEST_WITH_LOGS_BY_USER, {
            params,
            ...skipSpinnerOptions()
        });
    }

    getRequestStatusMetadataList(): Observable<RequestStatusMetadata[] | RequestStatusMetadataResponse> {
        return this.http.get<RequestStatusMetadata[] | RequestStatusMetadataResponse>(API_URLS.CONTACT_CENTER.GET_REQUEST_STATUS_METADATA_LIST, skipSpinnerOptions());
    }

    createContactCenterAssignment(payload: any): Observable<any> {
        let url = API_URLS.CONTACT_CENTER.CREATE_CONTACT_CENTER_ASSIGNMENT;
        if (payload?.reference_type === 'feedback') {
            url = API_URLS.CONTACT_CENTER.CREATE_CONTACT_CENTER_ASSIGNMENT_CHAT_FEEDBACK;
        } else if (payload?.reference_type === 'roleplay_feedback') {
            url = API_URLS.CONTACT_CENTER.CREATE_CONTACT_CENTER_ASSIGNMENT_ROLEPLAY;
        }
        return this.http.post<any>(url, payload, skipSpinnerOptions());
    }

    getAvailableCategories(): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.AVAILABLE_CATEGORIES, skipSpinnerOptions());
    }

    getAssignmentHistory(referenceId: number, referenceType: string): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.GET_ASSIGNMENT_HISTORY(referenceId), {
            ...skipSpinnerOptions(),
            params: { reference_type: referenceType }
        });
    }

    getFeedbacksByCategory(wwid: number, businessUnitCode: string): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.GET_FEEDBACKS_BY_CATEGORY, {
            ...skipSpinnerOptions(),
            params: { wwid: wwid.toString(), business_unit_code: businessUnitCode }
        });
    }

    getRoleplayFeedbacksByCategory(wwid: number, businessUnitCode: string): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.GET_ROLEPLAY_FEEDBACKS_BY_CATEGORY, {
            ...skipSpinnerOptions(),
            params: { wwid: wwid.toString(), business_unit_code: businessUnitCode }
        });
    }

    getRequestsByCategory(wwid: number, businessUnitCode: string): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.GET_REQUESTS_BY_CATEGORY, {
            ...skipSpinnerOptions(),
            params: { wwid: wwid.toString(), business_unit_code: businessUnitCode }
        });
    }

    getRequestorUsers(wwid: number, businessUnitCode: string, region: string, search: string, limit: number, offset: number): Observable<any> {
        return this.http.get<any>(API_URLS.CONTACT_CENTER.SEARCH_REQUESTOR_USERS, {
            ...skipSpinnerOptions(),
            params: { wwid: wwid.toString(), business_unit_code: businessUnitCode, search, region, limit: limit.toString(), offset: offset.toString() }
        });
    }

}
